import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ChipListSelect, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";
import {
  useListDoctorComplaintTemplatesMutation,
  useCreateDoctorComplaintTemplateMutation,
  useUpdateDoctorComplaintTemplateMutation,
  useSetDoctorComplaintTemplateStatusMutation,
} from "../../../store/doctorApi";
import { useListComplaintsMutation } from "../../../store/systemVariablesApi";

const SAMPLE_COMPLAINTS = [
  "Baş ağrısı", "Ateş", "Öksürük", "Bulantı", "Kusma", "İshal", "Karın ağrısı",
  "Halsizlik", "İştah kaybı", "Nefes darlığı", "Göğüs ağrısı", "Bel ağrısı",
  "Eklem ağrısı", "Boğaz ağrısı", "Baş dönmesi", "Uykusuzluk", "Kabızlık",
  "Kaşıntı", "Döküntü", "Kulak ağrısı",
];

export default function DoctorComplaintTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", items: [] });
  const [systemComplaints, setSystemComplaints] = useState([]);

  const bootRef = useRef(false);
  const [listDoctorComplaintTemplates] = useListDoctorComplaintTemplatesMutation();
  const [listComplaints] = useListComplaintsMutation();
  const [createDoctorComplaintTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorComplaintTemplateMutation();
  const [updateDoctorComplaintTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorComplaintTemplateMutation();
  const [setDoctorComplaintTemplateStatus] =
    useSetDoctorComplaintTemplateStatusMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorComplaintTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    listComplaints({ token, status: "active", limit: 500 })
      .unwrap()
      .then((res) =>
        setSystemComplaints(res?.data?.complaints || res?.data?.items || [])
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", items: [] });
    setOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({ name: template.name || "", items: template.items || [] });
    setOpen(true);
  };

  // ChipListSelect: şablon öğesi `{ text }`, sistem seçeneği `{ _id, name }`.
  // Aynı metin (büyük/küçük harf duyarsız) iki kez eklenmez.
  const complaintKey = (opt) => String(opt?.text ?? opt?.name ?? "").toLowerCase();

  const onComplaintsChange = (next) => {
    setForm((p) => {
      const prev = p.items || [];
      if (next.length > prev.length) {
        const value = next[next.length - 1]?.name || "";
        if (!value) return p;
        return { ...p, items: [...prev, { text: value }] };
      }
      return { ...p, items: next };
    });
  };

  const autoGenerate = () => {
    const pool = systemComplaints.length
      ? systemComplaints.map((c) => c.name)
      : SAMPLE_COMPLAINTS;
    const pick = (arr, n) =>
      [...arr].sort(() => Math.random() - 0.5).slice(0, Math.min(n, arr.length));
    const selected = pick(pool, Math.floor(Math.random() * 5) + 2);
    setForm({
      name: `Test Şikayet Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      items: selected.map((t) => ({ text: t })),
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    try {
      if (editing) {
        await updateDoctorComplaintTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorComplaintTemplate({
          token,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorComplaintTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => form.name.trim().length > 2, [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const itemNames = (row) => (row.items || []).map((i) => i?.text).filter(Boolean);

  return (
    <TemplateListPage
      icon={<ReportProblemIcon sx={{ fontSize: 22 }} />}
      title="Şikayet Şablonları"
      subtitle="Sık girilen şikayet gruplarını tek tıkla eklemek için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "34%" },
        { key: "items", label: "Şikayetler", width: "38%" },
        { key: "updated", label: "Güncellendi", width: "14%" },
        { key: "status", label: "Durum", width: "14%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const names = itemNames(row);
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          names.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {names.slice(0, 3).join(", ")}
              </Typography>
              <Typography sx={subText}>
                {names.length} şikayet{names.length > 3 ? ` · +${names.length - 3} daha` : ""}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<ReportProblemIcon />}
      emptyMessage="Sık girilen şikayet gruplarını şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<ReportProblemIcon />}
        title={editing ? "Şablonu düzenle" : "Yeni şikayet şablonu"}
        subtitle={editing ? editing.name : "Şikayetleri hızlıca eklemek için şablon oluşturun"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *" hint="En az 3 karakter.">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Üst solunum yolu"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <ChipListSelect
            label="Şikayetler"
            placeholder="Şikayet ara"
            options={systemComplaints}
            value={form.items || []}
            onChange={onComplaintsChange}
            getOptionId={complaintKey}
            getOptionLabel={(opt) => opt?.text ?? opt?.name ?? ""}
            emptyText="Henüz şikayet eklenmedi."
          />
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
